<template>
  <v-layout wrap>
    <helper-cropper
      ref="cropper"
      @updateAvatar="updateAvatar"
    />
    <v-flex
      :style="{ backgroundColor: form.bgcolor.hex || form.bgcolor}"
      xs12
      md12
      class="project-color"
    >
      <span
        id="colorOpener"
        @click="colorDialog = true"
      >{{ $t('projects.selectBg') }}</span>
      <chrome-picker
        v-show="colorDialog"
        v-model="form.bgcolor"
        v-on-clickaway="hideColorDialog"
      />
      <div
        class="avatar"
        :class="form.logo ? 'no-border': ''"
        @click.stop="$refs.image.click()"
      >
        <div v-if="!form.logo">
          <v-icon>mdi-cloud-upload</v-icon>
          <p>{{ $t('projects.upload') }}</p>
          <p>PNG / JPG</p>
          <small>150x150px</small>
        </div>
        <img
          v-if="form.logo"
          :src="form.logo"
          height="150"
        >
        <input
          ref="image"
          type="file"
          style="display: none"
          accept="image/*"
          @change="setImage"
        >
      </div>
    </v-flex>
    <v-flex
      xs12
      md8
    >
      <v-text-field
        id="project-name"
        v-model.trim="form.name"
        :counter="20"
        :error-messages="ProjectNameErrors"
        name="project-name"
        :label="$t('projects.projectName')+ '*'"
        required
        @input="$v.form.name.$touch()"
        @blur="$v.form.name.$touch()"
      />
    </v-flex>
    <v-flex
      xs12
      md4
    >
      <v-text-field
        id="project-ticker"
        v-model.trim="form.ticker"
        :counter="5"
        :error-messages="ProjectTickerErrors"
        name="project-ticker"
        :label="$t('projects.projectTicker')+ '*'"
        required
        :disabled="$route.params.id !== undefined"
        @input="$v.form.ticker.$touch()"
        @blur="$v.form.ticker.$touch()"
      >
        <template v-slot:append>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">
                mdi-information-outline
              </v-icon>
            </template>
            {{ $t('projects.tickerTooltip') }}
          </v-tooltip>
        </template>
      </v-text-field>
    </v-flex>
    <v-flex
      xs12
      md6
    >
      <v-select
        v-model.trim="form.project_type"
        :items="ProjectTypeItems"
        :item-text="'name'"
        :item-value="'id'"
        :item-disabled="'disabled'"
        :error-messages="ProjectTypeErrors"
        name="project-type"
        :label="$t('projects.strategieManagement')+ '*'"
        required
        @change="$v.form.project_type.$touch()"
        @blur="$v.form.project_type.$touch()"
      />
    </v-flex>
    <v-flex
      xs12
      md6
    >
      <v-select
        v-model.trim="form.view_permission"
        :items="ProjectViewItems"
        :item-text="'name'"
        :item-value="'id'"
        :error-messages="ProjectViewErrors"
        name="project-type"
        :label="$t('projects.strategyView')+ '*'"
        required
        @change="$v.form.view_permission.$touch()"
        @blur="$v.form.view_permission.$touch()"
      />
    </v-flex>
    <v-flex
      xs12
      md12
    >
      <v-autocomplete
        v-model="form.country"
        :items="countryList"
        :item-text="'name'"
        :item-value="'id'"
        :error-messages="CountryErrors"
        :label="$t('projects.country')+ '*'"
        required
        @input="$v.form.country.$touch()"
        @blur="$v.form.country.$touch()"
      />
    </v-flex>
    <v-flex
      xs12
      md12
    >
      <v-textarea
        id="project-description"
        v-model.trim="form.description"
        :counter="1000"
        :error-messages="ProjectDescriptionErrors"
        name="project-description"
        :label="$t('globals.description')+'*'"
        :placeholder="$t('projects.descPlaceholder')"
        required
        @input="$v.form.description.$touch()"
        @blur="$v.form.description.$touch()"
      />
    </v-flex>
    <v-flex
      xs12
      md12
    >
      <v-textarea
        id="management"
        v-model.trim="form.management"
        :counter="1000"
        :error-messages="ManagementErrors"
        name="management"
        :label="$t('projects.management')+'*'"
        :placeholder="$t('projects.managementPlaceholder')"
        required
        @input="$v.form.management.$touch()"
        @blur="$v.form.management.$touch()"
      />
    </v-flex>
  </v-layout>
</template>

<script>
import USER_WHITELABELS_QUERY from '@/graphql/queries/WhiteLabels.gql';
import { validationMixin } from "vuelidate";
import {
  required,
  minLength,
  maxLength,
  alphaNum
} from "vuelidate/lib/validators";
import { mapGetters, mapMutations } from "vuex";

import { directive as onClickaway } from "vue-clickaway";
import { Chrome } from "vue-color";

const alphanum_space = value => /^[a-zA-Z0-9 ]*$/.test(value);

export default {
  name: "FormProfile",
  directives: {
    onClickaway
  },
  components: {
    "chrome-picker": Chrome
  },
  mixins: [validationMixin],
  data() {
    return {
      ProjectTypeItems: [
        { id: "trader", name: this.$t("projects.trader") },
        { id: "bot", name: this.$t("projects.bot") },
      ],
      ProjectViewItems: [
        { id: "public", name: this.$t("projects.viewPublic")},
        { id: "link", name: this.$t("projects.viewLink")},
        { id: "hidden", name: this.$t("projects.viewPrivate")},
      ],
      dialog: false,
      colorDialog: false,
      imageName: "",
      imageFile: "",
      valid: false,
      form: {
        ownertype: 'private',
        whitelabel_uuid: null,
        view_permission: null,
        logo: null,
        bgcolor: this.$vuetify.theme.defaults.light.primary,
        name: null,
        ticker: null,
        country: null,
        type: 'trading.copy',
        project_type: null,
        description: null,
        management: null,
      }
    };
  },
  validations() {
    return {
      form: this.rules
    };
  },
  computed: {
    ...mapGetters("user", ["getUser"]),
    ...mapGetters("whitelabel", ["getWhiteLabel"]),
    countryList() {
      let list = require("@/lang/" + this.$i18n.locale + "/country.json");
      let arr = [];
      for (const key of _.keys(list)) {
        arr.push({ id: key, name: list[key] });
      }
      return arr;
    },
    rules() {
      var form = {
        name: {
          required,
          minLength: minLength(5),
          maxLength: maxLength(20),
          alphanum_space
        },
        ticker: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(5),
          alphaNum
        },
        country: {
          required
        },
        view_permission: {
          required
        },
        project_type: {
          required
        },
        description: {
          required,
          maxLength: maxLength(1000)
        },
        management: {
          required,
          maxLength: maxLength(1000)
        },
      };
      return form;
    },
    ProjectNameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      (!this.$v.form.name.minLength || !this.$v.form.name.maxLength) &&
        errors.push(this.$t("projects.maxName"));
      !this.$v.form.name.required && errors.push(this.$t("projects.reqName"));
      !this.$v.form.name.alphanum_space &&
        errors.push(this.$t("projects.onlyLatin"));
      return errors;
    },
    ProjectTickerErrors() {
      const errors = [];
      if (!this.$v.form.ticker.$dirty) return errors;
      (!this.$v.form.ticker.minLength || !this.$v.form.ticker.maxLength) &&
        errors.push(this.$t("projects.maxTicker"));
      !this.$v.form.ticker.required &&
        errors.push(this.$t("projects.reqTicker"));
      !this.$v.form.ticker.alphaNum &&
        errors.push(this.$t("projects.onlyLatin"));
      return errors;
    },
    ProjectTypeErrors() {
      const errors = [];
      if (!this.$v.form.project_type.$dirty) return errors;
      !this.$v.form.project_type.required && errors.push(this.$t("projects.reqField"));
      return errors;
    },
    ProjectViewErrors() {
      const errors = [];
      if (!this.$v.form.view_permission.$dirty) return errors;
      !this.$v.form.view_permission.required && errors.push(this.$t("projects.reqView"));
      return errors;
    },
    CountryErrors() {
      const errors = [];
      if (!this.$v.form.country.$dirty) return errors;
      !this.$v.form.country.required &&
        errors.push(this.$t("projects.reqCountry"));
      return errors;
    },
    ProjectDescriptionErrors() {
      const errors = [];
      if (!this.$v.form.description.$dirty) return errors;
      (!this.$v.form.description.maxLength) &&
        errors.push(this.$t("projects.maxDesc"));
      !this.$v.form.description.required &&
        errors.push(this.$t("projects.reqDesc"));
      return errors;
    },
    ManagementErrors() {
      const errors = [];
      if (!this.$v.form.management.$dirty) return errors;
      (!this.$v.form.management.maxLength) &&
        errors.push(this.$t("projects.100400Field"));
      !this.$v.form.management.required &&
        errors.push(this.$t("projects.reqField"));
      return errors;
    },
  },
  mounted() {
    this.form.whitelabel_uuid = this.getWhiteLabel.uuid
    this.$store.subscribe((mutation, state) => {
      if (mutation.type == "project/setProject") {
        this.form = state.project.form;
      }
    });
  },
  methods: {
    ...mapMutations("project", ["saveProfile", "setTab"]),
    save() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.form.ticker = this.form.ticker.toUpperCase();
        this.saveProfile(this.form);
      }
    },
    setImage(e) {
      if (!this.$refs.cropper.setImage(e)) {
        this.$refs.image.value = "";
      }
    },
    updateAvatar(avatar) {
      this.form.logo = avatar;
    },
    hideColorDialog(e) {
      if (e) e.preventDefault();
      if (this.colorDialog && e.target.id !== "colorOpener") {
        this.colorDialog = false;
      }
    },
  },
  apollo: {
    whiteLabels: {
      query: USER_WHITELABELS_QUERY,
      variables() {
        return {
          owner_uuid: this.getUser.uuid
        }
      },
      skip() {
        return !this.getUser
      }
    },
  }
};
</script>

<style lang="scss">
.project-color {
  background-color: #0a61f0;
  height: 140px;
  margin-bottom: 50px;
  .avatar {
    position: relative;
    left: 50%;
    top: 40px;
    transform: translate(-50%, 0);
    width: 150px;
    height: 150px;
    background: #fff;
    border: 1px solid #0a61f0;
    border-radius: 150px;
    text-align: center;
    cursor: pointer;
    overflow: hidden;
    z-index: 1;
    i {
      margin: 20px 0 5px;
      color: #9c27b0;
      font-size: 26px;
    }
    p {
      margin: 7px 0 0!important;
      &:first-of-type {
        font-weight: 700;
        color: #9c27b0;
      }
      color: rgba(0, 0, 0, 0.87);
      line-height: 0.85rem;
    }
    small {
      font-size: 11px;
    }
    &.no-border {
      border: 0 !important;
    }
  }
  .vc-chrome {
    position: absolute;
    z-index: 100;
    top: 140px;
    right: 0px;
    float: right;
  }
  span {
    position: relative;
    float: right;
    top: 140px;
    font-size: 12px;
    font-weight: 400;
    color: #0a61f0;
    cursor: pointer;
  }
}
</style>
